/**
 * @file VideoPreviewV2.tsx
 *
 * @description A component for displaying and interacting with police video evidence.
 * This component renders police videos with optional transcript data overlaid on the video.
 * It supports both single video and paginated multi-video displays.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 */

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  useTheme,
  Pagination,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  alpha,
  Slider,
  CircularProgress,
  Collapse,
  Menu,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ExpandIcon from '@mui/icons-material/Expand';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ChatIcon from '@mui/icons-material/Chat';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from "@mui/icons-material/Download";
import { VideoData } from "../../shared/interfaces/VideoData";
import audioThumbnailLight from "../../assets/audio_thumbnail_light.svg";
import audioThumbnailDark from "../../assets/audio_thumbnail_dark.svg";
import { TranscriptData } from "./TranscriptViewer";
import { TranscriptOverlay } from "./TranscriptOverlay";
import { formatEvidenceDateTime } from "./formatEvidenceDateTime";
// import transcriptTemplateHtml from "./transcriptTemplate.html?raw";
import transcriptTemplateDocxUrl from "./transcript-template.docx?url";

/**
 * Props for the VideoPreview component
 * @interface VideoPreviewProps
 * @property {VideoData | VideoData[]} videoData - Single video or array of videos to display
 * @property {boolean} [isMinimized=false] - Whether the component is in minimized view mode
 * @property {() => void} [onToggleMinimize] - Callback function triggered when minimize/maximize button is clicked
 * @property {boolean} [isMinimizable=true] - Whether the component can be minimized/maximized
 */
interface VideoPreviewProps {
  videoData: VideoData | VideoData[];
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  isMinimizable?: boolean;
}

/**
 * @name VideoPreviewV2
 *
 * @description VideoPreviewV2 component for displaying video evidence with OVERLAID transcript
 *
 * This V2 component displays video evidence with its associated information including
 * recording date, upload date, and evidence ID. Unlike V1, the transcript is
 * displayed as a semi-transparent overlay on top of the video content.
 *
 * @param {VideoPreviewProps} props - Component props
 *
 * @returns {React.ReactElement} The rendered VideoPreview component
 */
/** Imperative API for driving the player from outside (e.g. citation clicks). */
export interface VideoPreviewHandle {
  /** Switch to the video for `evidenceId` (if present) and seek to `seconds`. */
  seekToEvidence: (evidenceId: string, seconds: number) => void;
}

export const VideoPreviewV2 = forwardRef<VideoPreviewHandle, VideoPreviewProps>(
  function VideoPreviewV2(
    { videoData, isMinimized = false, onToggleMinimize, isMinimizable = true },
    ref,
  ) {
  const [page, setPage] = useState(0); // if multiple videos are to be shown, track the current page
  const theme = useTheme();
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // Current video playback time
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const [isVideoLoading, setIsVideoLoading] = useState(true); // Video loading state
  const [isTransitioning, setIsTransitioning] = useState(false); // Page transition state
  const [showTranscriptOverlay, setShowTranscriptOverlay] = useState(true); // Toggle for overlay
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  
  const videoRef = useRef<HTMLVideoElement | null>(null); // Reference to the video element for programmatic control
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // A seek requested before the target video's <video> element is ready (e.g.
  // after a page switch). Applied in handleVideoCanPlay once it can play.
  const pendingSeekRef = useRef<number | null>(null);

  // Imperative seek-to-evidence used by citation anchors: switch to the video
  // for `evidenceId` (paginating if needed) and seek to `seconds`.
  const seekToEvidence = useCallback(
    (evidenceId: string, seconds: number) => {
      const videos = Array.isArray(videoData) ? videoData : [videoData];
      const index = videos.findIndex((video) => video.id === evidenceId);
      if (index === -1) return;

      // Expanding (un-minimizing) is the caller's responsibility — the video
      // element stays mounted while minimized, so the seek itself works either way.
      if (Array.isArray(videoData) && index !== page) {
        pendingSeekRef.current = seconds;
        setIsVideoLoading(true);
        setPage(index);
      } else if (videoRef.current) {
        try {
          videoRef.current.currentTime = seconds;
        } catch {
          /* seeking before metadata is harmless; ignore */
        }
        setCurrentTime(seconds);
      } else {
        // Target video element not mounted yet (e.g. first render); apply the
        // seek once it can play so the citation isn't dropped.
        pendingSeekRef.current = seconds;
        setIsVideoLoading(true);
      }

      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [videoData, page],
  );

  useImperativeHandle(ref, () => ({ seekToEvidence }), [seekToEvidence]);

  // Helper to format time
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Video Handlers
  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      videoRef.current.muted = newVolume === 0;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      setIsMuted(newMuted);
      videoRef.current.muted = newMuted;
      if (newMuted) {
        setVolume(0);
      } else {
        setVolume(1);
        videoRef.current.volume = 1;
      }
    }
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    const time = Array.isArray(newValue) ? newValue[0] : newValue;
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  }, []);

  // Listen for fullscreen chagne events (in case user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Controls Visibility
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  // Pause when minimizing
  const handleToggleMinimize = () => {
    if (!isMinimized && isPlaying) {
      handlePlayPause();
    }
    if (onToggleMinimize) onToggleMinimize();
  }

  // Memoized time update handler
  const handleTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      setCurrentTime(e.currentTarget.currentTime);
    },
    []
  );

  // Handle video load start/end
  const handleVideoLoadStart = useCallback(() => {
    setIsVideoLoading(true);
  }, []);

  const handleVideoCanPlay = useCallback(() => {
    setIsVideoLoading(false);
    if (videoRef.current) {
        setDuration(videoRef.current.duration);
        if (pendingSeekRef.current != null) {
          try {
            videoRef.current.currentTime = pendingSeekRef.current;
          } catch {
            /* ignore */
          }
          setCurrentTime(pendingSeekRef.current);
          pendingSeekRef.current = null;
        }
    }
  }, []);

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  /**
   * @name handlePageChange
   *
   * @description Handles page change in paginated video display with smooth transition
   *
   * @param {React.ChangeEvent<unknown>} _ - Unused event parameter
   * @param {number} value - New page number (1-indexed)
   */
  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setIsTransitioning(true);
      setTimeout(() => {
        setPage(value - 1);
        setIsVideoLoading(true);
        setIsTransitioning(false);
        // Reset transcript overlay state or keep user preference? Keep preference.
      }, 150);
    },
    []
  );

  // Reusable styles for metadata cards
  const metadataCardSx = {
    backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50],
    p: 1.5,
    borderRadius: 2,
    height: "100%",
    border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : theme.palette.grey[200]}`,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.05) : theme.palette.grey[100],
      borderColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.2) : theme.palette.grey[300],
    },
  };

  /**
   * Formats seconds into MM:SS timestamp format
   */
  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };


  /**
   * @name handleDownloadTranscriptTxt
   * @description Builds a plain-text transcript and triggers a .txt file download
   * @param {VideoData} video - The video data
   */
  const handleDownloadTranscriptTxt = (video: VideoData) => {
    if (!video.transcript) return;
    const transcriptData = video.transcript as TranscriptData;
    const lines: string[] = [];

    if (video.title) lines.push(`Title: ${video.title}`);
    if (video.id) lines.push(`ID: ${video.id}`);
    if (video.recordedOn) lines.push(`Recorded On: ${formatEvidenceDateTime(video.recordedOn)}`);
    if (video.recordedBy) lines.push(`Recorded By: ${video.recordedBy}`);
    lines.push("");

    for (const p of transcriptData.paragraphs ?? []) {
      lines.push(`[${formatTimestamp(p.start)} - ${formatTimestamp(p.end)}] Speaker ${p.speaker}`);
      lines.push(p.sentences.map((s) => s.text).join(" "));
      lines.push("");
    }

    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transcript-${video.title ?? "export"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // const handleDownloadTranscriptPdf = async (video: VideoData) => {
  //   if (!video.transcript) return;
  //   try {
  //     const [Handlebars, html2pdf] = await Promise.all([
  //       import("handlebars").then((m) => m.default),
  //       import("html2pdf.js").then((m) => m.default),
  //     ]);

  //     const transcriptData = video.transcript as TranscriptData;
  //     const paragraphs = transcriptData.paragraphs || [];

  //     const processedParagraphs = paragraphs.map((p) => ({
  //       ...p,
  //       start: p.start.toFixed(2),
  //       end: p.end.toFixed(2),
  //     }));

  //     const template = Handlebars.compile(transcriptTemplateHtml);
  //     const filledHtml = template({
  //       title: video.title,
  //       id: video.id,
  //       videoUrl: video.videoUrl,
  //       status: video.status,
  //       recordedOn: convertUTCToLocal(video.recordedOn),
  //       recordedBy: video.recordedBy,
  //       uploadedOn: convertUTCToLocal(video.uploadedOn),
  //       uploadedBy: video.uploadedBy,
  //       transcript: { ...transcriptData, paragraphs: processedParagraphs },
  //     });

  //     const container = document.createElement("div");
  //     container.innerHTML = filledHtml;

  //     await html2pdf()
  //       .set({
  //         margin: 0,
  //         filename: `transcript-${video.id}.pdf`,
  //         image: { type: "jpeg", quality: 0.98 },
  //         html2canvas: { scale: 2, useCORS: true },
  //         jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  //       })
  //       .from(container)
  //       .save();
  //   } catch (error) {
  //     console.error("Failed to generate PDF:", error);
  //   }
  // };

  const handleDownloadDocx = async (video: VideoData) => {
    if (!video.transcript) return;
    try {
      const [PizZipModule, DocxtemplaterModule, { saveAs }] = await Promise.all(
        [import("pizzip"), import("docxtemplater"), import("file-saver")],
      );
      const PizZip = PizZipModule.default;
      const Docxtemplater = DocxtemplaterModule.default;

      const response = await fetch(transcriptTemplateDocxUrl);
      if (!response.ok)
        throw new Error(`Template fetch failed: ${response.status}`);
      const templateBuffer = await response.arrayBuffer();

      const transcriptData = video.transcript as TranscriptData;
      const paragraphs = (transcriptData.paragraphs || []).map((p) => ({
        speaker: p.speaker,
        start: p.start.toFixed(2),
        end: p.end.toFixed(2),
        text: p.sentences.map((s) => s.text).join(" "),
      }));

      const zip = new PizZip(templateBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        delimiters: { start: "{{", end: "}}" },
      });
      doc.setData({
        title: video.title,
        id: video.id,
        videoUrl: video.videoUrl,
        status: video.status,
        recordedOn: video.recordedOn
          ? formatEvidenceDateTime(video.recordedOn)
          : "",
        recordedBy: video.recordedBy ?? "",
        uploadedOn: video.uploadedOn
          ? formatEvidenceDateTime(video.uploadedOn)
          : "",
        uploadedBy: video.uploadedBy ?? "",
        paragraphs,
      });
      doc.render();

      const blob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      saveAs(blob, `transcript-${video.title ?? "export"}.docx`);
    } catch (error) {
      console.error("Failed to generate DOCX:", error);
    }
  };

  /**
   * @name renderVideo
   *
   * @description Renders a single video card with metadata and transcript
   *
   * @param {VideoData} videoData - Video data to display
   *
   * @returns {React.ReactElement} Rendered video card
   */
  const renderVideo = (videoData: VideoData) => {
    const isAudio = videoData.fileType?.startsWith("audio") ?? false;
    const audioThumbnail = theme.palette.mode === "dark" ? audioThumbnailDark : audioThumbnailLight;
    return (
    <Fade in={!isTransitioning} timeout={200}>
      <Box>
        {/* Minimized View — compact timed scrubber. The expanded player (and its
            <video>) stays mounted below, so play/seek (and citation seeks) keep
            working while minimized; this behaves like a slim audio player. */}
        <Collapse in={isMinimized}>
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              p: 1,
              borderRadius: 2,
              background: theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
              border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[200]}`,
            }}
          >
            <Tooltip title={isPlaying ? "Pause" : "Play"} arrow>
              <IconButton
                size="small"
                onClick={handlePlayPause}
                sx={{ color: theme.palette.primary.main, flexShrink: 0 }}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={videoData.title} arrow placement="top">
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 140,
                  flexShrink: 0,
                  color: theme.palette.text.primary,
                }}
              >
                {videoData.title}
              </Typography>
            </Tooltip>
            <Slider
              size="small"
              value={currentTime}
              max={duration || 0}
              onChange={handleSeek}
              aria-label="Seek"
              sx={{ mx: 1, flexGrow: 1 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
            {isMinimizable && (
              <Tooltip title="Expand video" arrow>
                <IconButton size="small" onClick={handleToggleMinimize} sx={{ flexShrink: 0 }}>
                  <ExpandIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Paper>
        </Collapse>

        {/* Expanded View (YouTube-style Player) — kept mounted while minimized so
            the <video> element (audio + seek target) persists. */}
        <Collapse in={!isMinimized} timeout={500}>
            <Box>
              <Paper
                ref={containerRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                  position: "relative",
                  backgroundColor: "#000",
                  aspectRatio: "16/9",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  ...(isAudio && {
                    backgroundImage: `url(${audioThumbnail})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }),
                }}
              >
                {/* 1. Video Element */}
                <video
                  ref={videoRef}
                  width="100%"
                  height="100%"
                  src={videoData.videoUrl}
                  poster={isAudio ? audioThumbnail : undefined}
                  onClick={handlePlayPause}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadStart={handleVideoLoadStart}
                  onCanPlay={handleVideoCanPlay}
                  onEnded={handleVideoEnded}
                  playsInline
                  style={{ display: "block", objectFit: "contain" }}
                />

                {/* 2. Top Controls Overlay */}
                <Fade in={showControls || !isPlaying}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      p: 2,
                      background: "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      zIndex: 2,
                      pointerEvents: "none", // let clicks pass through to video
                    }}
                  >
                    <Typography 
                        variant="h6" 
                        sx={{ color: "white", textShadow: "0 1px 2px rgba(0,0,0,0.8)", pointerEvents: "auto" }}
                    >
                        {videoData.title}
                    </Typography>
                    
                    <Box sx={{ pointerEvents: "auto", display: 'flex', gap: 1 }}>
                        {/* Top Right Controls if any */}
                    </Box>
                  </Box>
                </Fade>

                {/* 3. Center Info/Action Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  {isVideoLoading ? (
                    <CircularProgress size={60} sx={{ color: "white" }} />
                  ) : (
                    <Fade in={!isPlaying}>
                        <Box
                           onClick={handlePlayPause} 
                           sx={{ 
                               pointerEvents: "auto", 
                               borderRadius: '50%', 
                               bgcolor: 'rgba(0, 0, 0, 0.5)',
                               p: 2,
                               cursor: 'pointer',
                               transition: 'transform 0.1s',
                               '&:active': { transform: 'scale(0.95)' }
                           }}
                        >
                            <PlayArrowIcon sx={{ fontSize: 60, color: "white" }} />
                        </Box>
                    </Fade>
                  )}
                </Box>

                {/* 4. Transcript Overlay */}
                {!!videoData.transcript && showTranscriptOverlay && (
                    <TranscriptOverlay 
                        transcript={videoData.transcript as TranscriptData}
                        currentTimeStamp={currentTime}
                        handleParagraphClick={(timestamp) => {
                            if (videoRef.current) {
                                videoRef.current.currentTime = timestamp;
                            }
                        }}
                        sx={{ bottom: showControls ? 90 : 24, transition: 'bottom 0.3s ease' }}
                    />
                )}

                {/* 5. Bottom Controls Bar */}
                <Fade in={showControls || !isPlaying}>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
                      px: 2,
                      pb: 1.5,
                      pt: 4,
                      zIndex: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {/* Timeline Slider */}
                    <Slider
                      value={currentTime}
                      min={0}
                      max={duration || 100}
                      onChange={handleSeek}
                      size="small"
                      sx={{
                        color: theme.palette.error.main, // YouTube red
                        height: 4,
                        p: 0,
                        "& .MuiSlider-thumb": {
                          width: 12,
                          height: 12,
                          transition: "0.2s",
                          "&:before": { boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)" },
                          "&:hover, &.Mui-focusVisible": { boxShadow: `0px 0px 0px 8px ${alpha(theme.palette.error.main, 0.16)}` },
                        },
                        "& .MuiSlider-rail": { opacity: 0.3, backgroundColor: "#fff" },
                      }}
                    />
                    
                    {/* Controls Row */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton onClick={handlePlayPause} sx={{ color: "white" }} size="small">
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .volume-slider': { width: 100, opacity: 1, ml: 1 } }}>
                                <IconButton onClick={toggleMute} sx={{ color: "white" }} size="small">
                                    {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                                </IconButton>
                                <Box className="volume-slider" sx={{ width: 0, opacity: 0, overflow: 'hidden', transition: 'all 0.2s', ml: 0 }}>
                                    <Slider 
                                        value={isMuted ? 0 : volume} 
                                        min={0} max={1} step={0.1} 
                                        onChange={handleVolumeChange}
                                        sx={{ color: "white", height: 2, width: '90%' }}
                                    />
                                </Box>
                            </Box>

                            {/* Transcript Toggle */}
                            {!!videoData.transcript && (
                                <Tooltip title={showTranscriptOverlay ? "Hide Transcript" : "Show Transcript"}>
                                    <IconButton 
                                        onClick={() => setShowTranscriptOverlay(!showTranscriptOverlay)}
                                        sx={{ color: "white" }}
                                        size="small"
                                    >
                                        {showTranscriptOverlay ? <ChatIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            
                            <Typography variant="body2" sx={{ color: "white", ml: 1, fontFamily: 'monospace' }}>
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton onClick={handleFullscreen} sx={{ color: "white" }} size="small">
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </IconButton>
                            {/* Minimize Button */}
                            {isMinimizable && (
                                <Tooltip title="Minimize view">
                                    <IconButton onClick={handleToggleMinimize} sx={{ color: "white" }} size="small">
                                        <UnfoldLessIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                  </Box>
                </Fade>
              </Paper>

              {/* Video metadata - Compact Row */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {/* Recording date */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={metadataCardSx}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={700}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: "0.8rem" }} /> RECORDED
                      </Typography>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {formatEvidenceDateTime(videoData.recordedOn)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Upload date */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={metadataCardSx}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={700}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        <CloudUploadIcon sx={{ fontSize: "0.8rem" }} /> UPLOADED
                      </Typography>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {formatEvidenceDateTime(videoData.uploadedOn)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Evidence ID */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={metadataCardSx}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={700}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        <FingerprintIcon sx={{ fontSize: "0.8rem" }} /> ID
                      </Typography>
                    <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ fontFamily: 'monospace' }}>
                      {videoData.id}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {/* Download transcript button */}
              {!!videoData.transcript && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Tooltip title="Choose download format" arrow>
                    <Box
                      component="button"
                      onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 0.75,
                        border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : theme.palette.grey[300]}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.05)
                          : theme.palette.grey[50],
                        color: theme.palette.text.secondary,
                        cursor: 'pointer',
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: "0.9rem" }} />
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: '0.3px', color: 'inherit' }}>
                        Download Transcript
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Menu
                    anchorEl={downloadMenuAnchor}
                    open={Boolean(downloadMenuAnchor)}
                    onClose={() => setDownloadMenuAnchor(null)}
                    onClick={() => setDownloadMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => handleDownloadTranscriptTxt(videoData)}>
                      Download as TXT
                    </MenuItem>
                    <MenuItem onClick={() => handleDownloadDocx(videoData)}>
                      Download as DOCX
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Box>
        </Collapse>
      </Box>
    </Fade>
    );
  };

  // Render multiple videos with pagination or a single video
  if (Array.isArray(videoData)) {
    return (
      <Box>
        {renderVideo(videoData[page])}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            p: 1,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50],
            border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[200]}`,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 2 }}
          >
            Video {page + 1} of {videoData.length}
          </Typography>
          <Pagination
            count={videoData.length}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              },
            }}
          />
        </Paper>
      </Box>
    );
  }

  return renderVideo(videoData);
});
